import { useEffect, useState } from "react";
import {
  CompleteArray,
  DefaultFlowTemplateData,
} from "../../../types/FlowTemplates";

import {
  useAddFlowTemplateMutation,
  useDeleteFlowTemplateMutation,
  useUpdateFlowTemplateMutation,
} from "../../../store/apiSlices/flowTemplates";

import EditableField from "./EditableField";
import {
  ArrowUTurnLeft,
  CrossIcon,
  DocumentTextIcon,
  SpinnerIcon,
  TrashIcon,
} from "../../Icons";
import { toast } from "react-toastify";
import EditTemplateTitle from "./EditTemplateTitle";
import FlowBuilder from "./FlowBuilder";

export default function ActiveFlowTemplateCard({
  activeTemplate,
  onClose,
}: {
  activeTemplate: DefaultFlowTemplateData;
  onClose: () => void;
}) {
  const [steps, setSteps] = useState<CompleteArray[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [initialSteps, setInitialSteps] = useState<CompleteArray[]>([]);

  const [titleValue, setTitleValue] = useState("");

  const [addFlowTemplate, { isLoading: addLoading }] =
    useAddFlowTemplateMutation();

  const [updateFlowTemplate, { isLoading: updateLoading }] =
    useUpdateFlowTemplateMutation();

  const [deleteFlowTemplate] = useDeleteFlowTemplateMutation();

  useEffect(() => {
    const mutableActiveTemplate = JSON.parse(
      JSON.stringify(activeTemplate),
    ) as DefaultFlowTemplateData;

    setTitleValue(mutableActiveTemplate.title);
    setSteps(mutableActiveTemplate.complete_array);
    setInitialSteps(mutableActiveTemplate.complete_array);

    return () => {
      setSteps([]);
      setInitialSteps([]);
    };
  }, [activeTemplate]);

  const handleOnDropStep = (idx: number) => {
    if (dragIndex === null || dragIndex === idx) return;
    const updated = [...steps];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(idx, 0, moved);
    setSteps(updated);
    setDragIndex(null);
  };

  const insertStep = (idx: number) => {
    const newStep: CompleteArray = {
      step: { field: "New Step", complted: false },
      Contacts: [],
      tasks: [],
      document_vault: [],
    };
    const updated = [...steps];
    updated.splice(idx + 1, 0, newStep);
    setSteps(updated);
  };

  const handleRemoveStep = (idx: number) => {
    const updated = [...steps];
    updated.splice(idx, 1);
    setSteps(updated);
  };

  const handleSave = async () => {
    if (activeTemplate && activeTemplate.id && activeTemplate.uid) {
      const { id, type, uid } = activeTemplate;

      const payload = {
        id,
        uid,
        title: titleValue,
        type,
        complete_array: steps,
      };

      await updateFlowTemplate(payload);
    } else {
      const { title, type } = activeTemplate;

      if (titleValue && title === titleValue) {
        toast.warning(
          `You can not save a new template with the same ${title} title! Double click on it to chnage the title`,
        );

        return;
      }

      const payload = {
        title: titleValue,
        type,
        complete_array: steps,
      };
      // console.log(payload);
      const { data } = await addFlowTemplate(payload);
      if (data.status === "success" && data.id) {
        toast.success(data.message);
        onClose();
      } else {
        toast.error("Something went wrong! try again.");
      }
    }
  };

  const handleDelete = async (id: number) => {
    onClose();
    await deleteFlowTemplate(id);
  };

  return (
    <div className="bg-white text-gray-800 rounded-xl p-6 border border-[#f3f3f5] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EditTemplateTitle title={titleValue} setTitleValue={setTitleValue} />
          {activeTemplate.uid ? (
            <p className="bg-blue-50 text-blue-400 text-xs font-medium px-2 py-[1px] rounded-xl border border-blue-400">
              Custom
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-5">
          <button
            onClick={handleSave}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={addLoading || updateLoading}
          >
            {addLoading || updateLoading ? (
              <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
            ) : (
              <DocumentTextIcon className="size-4" />
            )}
            {activeTemplate.uid ? "Update" : "Save"}
          </button>

          <button
            onClick={() => {
              setSteps(initialSteps);
            }}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={addLoading || updateLoading}
          >
            <ArrowUTurnLeft className="size-4" />
            Reset
          </button>

          {activeTemplate.uid ? (
            <button
              onClick={() => handleDelete(activeTemplate.id)}
              className="p-1 rounded-lg hover:text-red-600 hover:scale-90 hover:bg-gray-100"
            >
              <TrashIcon className="size-6" />
            </button>
          ) : null}

          <button
            onClick={onClose}
            className="hover:scale-90 hover:text-red-400"
          >
            <CrossIcon className="size-6" />
          </button>
        </div>
      </div>

      <FlowBuilder>
        <FlowBuilder.Track>
          {steps.map((el, idx) => (
            <FlowBuilder.Item key={idx}>
              <FlowBuilder.Step
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleOnDropStep(idx)}
              >
                <FlowBuilder.Badge>{idx + 1}</FlowBuilder.Badge>

                <EditableField
                  {...{ el, idx, steps, setSteps, initialSteps }}
                />

                {!["Shipment Pickup", "Shipment Booking"].includes(
                  el.step.field,
                ) && (
                  <FlowBuilder.RemoveBtn
                    onClick={() => handleRemoveStep(idx)}
                  />
                )}
              </FlowBuilder.Step>

              <FlowBuilder.AddBtn onClick={() => insertStep(idx)} />
            </FlowBuilder.Item>
          ))}
        </FlowBuilder.Track>
      </FlowBuilder>
    </div>
  );
}
